/**
 * MM Diagnostic
 * Navigation typeform entre les étapes, calcul du score, redirect vers la page résultat.
 * État persisté en sessionStorage (survie au refresh, pas au nouvel onglet).
 */

class MMDiagnostic {
  constructor(el) {
    this.el = el;
    this.sectionId = el.dataset.sectionId;
    this.totalQuestions = parseInt(el.dataset.totalQuestions, 10);
    this.scoreMax = parseInt(el.dataset.scoreMax, 10) || 100;

    this.steps = {
      1: el.querySelector('[data-step="1"]'),
      2: el.querySelector('[data-step="2"]'),
      3: el.querySelector('[data-step="3"]'),
    };

    this.questions = [...el.querySelectorAll('.mm-diag__question')];
    this.progressFill = el.querySelector(`#diag-progress-fill-${this.sectionId}`);
    this.progressBar = el.querySelector(`#diag-progress-bar-${this.sectionId}`);
    this.progressLabel = el.querySelector(`#diag-progress-label-${this.sectionId}`);

    this.ranges = this._loadRanges();
    this.storageKey = `mm-diag-${this.sectionId}`;
    this.state = this._loadState();

    this._restoreState();
    this._bindEvents();
  }

  /* ---- State ---- */

  _defaultState() {
    return { step: 1, currentQuestion: 1, answers: {}, totalScore: 0 };
  }

  _loadState() {
    try {
      const saved = sessionStorage.getItem(this.storageKey);
      return saved ? JSON.parse(saved) : this._defaultState();
    } catch {
      return this._defaultState();
    }
  }

  _saveState() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch {}
  }

  _clearState() {
    try { sessionStorage.removeItem(this.storageKey); } catch {}
    this.state = this._defaultState();
  }

  /* ---- Restore on page load ---- */

  _restoreState() {
    this.el.dataset.activeStep = this.state.step;
    if (this.state.step === 1) return;
    this._showStep(this.state.step, false);
    if (this.state.step === 2) {
      this._showQuestion(this.state.currentQuestion, false);
      this._restoreAnswers();
      const currentQ = this.questions[this.state.currentQuestion - 1];
      if (currentQ) this._updateNextBtn(currentQ);
    }
  }

  _restoreAnswers() {
    Object.entries(this.state.answers).forEach(([qIndex, data]) => {
      const qEl = this.questions[parseInt(qIndex, 10) - 1];
      if (!qEl) return;
      const qType = qEl.dataset.type;

      if (qType === 'single' || qType === 'multiple') {
        const scores = Array.isArray(data) ? data : [];
        qEl.querySelectorAll('.mm-diag__answer').forEach((a) => {
          if (scores.includes(parseInt(a.dataset.score, 10))) {
            a.classList.add('is-selected');
            a.setAttribute('aria-pressed', 'true');
          }
        });
      } else if (qType === 'matrix') {
        // data = { subIndex: score }
        Object.entries(data).forEach(([subIdx, score]) => {
          const row = qEl.querySelector(`.mm-diag__matrix-container[data-sub="${subIdx}"]`);
          if (!row) return;
          row.querySelectorAll('.mm-diag__matrix-cell').forEach((cell) => {
            if (parseInt(cell.dataset.score, 10) === score) {
              cell.classList.add('is-selected');
              cell.setAttribute('aria-pressed', 'true');
            }
          });
        });
      }
    });
  }

  /* ---- Navigation ---- */

  _showStep(step, animate = true) {
    Object.values(this.steps).forEach((s) => { if (s) s.dataset.active = 'false'; });
    if (this.steps[step]) this.steps[step].dataset.active = 'true';
    this.el.dataset.activeStep = step;
    this.state.step = step;
    this._saveState();
    if (step === 3) {
      if (this.progressFill) this.progressFill.style.width = '100%';
      if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', 100);
      if (this._hasSubmitted()) {
        const rawScore = this._computeTotalScore();
        this.state.totalScore = Math.round((rawScore / this.scoreMax) * 100);
        const redirected = this._handleKlaviyoSubmit();
        if (redirected) return;
        window.log('warn', '🎯 [DIAG]', 'No result range matched score, falling back to Klaviyo form', { score: this.state.totalScore });
      }
      this._teleportKlaviyoBtn();
    }
  }

  _teleportKlaviyoBtn() {
    const formEl = this.el.querySelector('[data-diag-klaviyo-form]');
    if (!formEl) return;

    const existing = document.querySelector('.mm-diag__klaviyo-cta');
    if (existing) return;

    let settled = false;

    const tryMove = () => {
      const btn = formEl.querySelector('button[type="submit"], button:not([type="button"])');
      if (!btn) return false;

      settled = true;
      const cta = document.createElement('div');
      cta.className = 'mm-diag__klaviyo-cta';
      cta.appendChild(btn);
      document.body.appendChild(cta);
      window.log('info', '🎯 [DIAG]', 'Klaviyo submit btn teleported to body');
      return true;
    };

    if (tryMove()) return;

    const observer = new MutationObserver(() => {
      if (tryMove()) observer.disconnect();
    });
    observer.observe(formEl, { childList: true, subtree: true });

    // Klaviyo dispatche "embedOpen" quand le form embed s'affiche réellement —
    // signal officiel documenté, contrairement à son localStorage interne.
    const onEmbedOpen = (e) => {
      if (e.detail?.type === 'embedOpen') settled = true;
    };
    window.addEventListener('klaviyoForms', onEmbedOpen);

    // Filet de sécurité : Klaviyo ne réaffiche jamais un formulaire déjà soumis
    // et ne dispatche aucun événement pour le signaler (comportement non documenté
    // par Klaviyo). Si rien ne s'est passé après ce délai, on considère que le
    // formulaire ne s'affichera pas et on redirige directement vers le résultat.
    setTimeout(() => {
      window.removeEventListener('klaviyoForms', onEmbedOpen);
      if (settled) return;
      observer.disconnect();
      window.log('warn', '🎯 [DIAG]', 'Klaviyo form never appeared, redirecting to result as fallback');
      const rawScore = this._computeTotalScore();
      this.state.totalScore = Math.round((rawScore / this.scoreMax) * 100);
      this._handleKlaviyoSubmit();
    }, 2500);
  }

  _showQuestion(index, animate = true) {
    const prev = this.el.querySelector('.mm-diag__question[data-active="true"]');
    if (prev) prev.dataset.active = 'false';

    const next = this.questions[index - 1];
    if (!next) return;

    if (animate) {
      next.style.animation = 'none';
      next.offsetHeight; // reflow pour relancer l'animation
      next.style.animation = '';
    }

    next.dataset.active = 'true';
    this.state.currentQuestion = index;
    this._updateProgress(index);
    this._saveState();
    this._updateNextBtn(next);
  }

  _updateProgress(currentIndex) {
    // Dénominateur = N questions + 1 (étape email) — la barre atteint 100% à l'étape email
    const total = this.totalQuestions + 1;
    const pct = Math.round((currentIndex / total) * 100);
    if (this.progressFill) this.progressFill.style.width = `${pct}%`;
    if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', pct);
  }

  /* ---- Scoring ---- */

  _computeTotalScore() {
    return Object.values(this.state.answers).reduce((sum, data) => {
      if (Array.isArray(data)) {
        return sum + data.reduce((s, v) => s + v, 0);
      }
      // matrix : objet { subIndex: score }
      return sum + Object.values(data).reduce((s, v) => s + v, 0);
    }, 0);
  }

  _findResultUrl(score) {
    const match = this.ranges.find((r) => score >= r.min && score <= r.max);
    return match ? match.url : null;
  }

  _hasSubmitted() {
    try { return !!localStorage.getItem(`mm-diag-submitted-${this.sectionId}`); } catch { return false; }
  }

  _markSubmitted() {
    try { localStorage.setItem(`mm-diag-submitted-${this.sectionId}`, '1'); } catch {}
  }

  _loadRanges() {
    const el = document.getElementById(`diag-ranges-${this.sectionId}`);
    if (!el) return [];
    let raw;
    try { raw = JSON.parse(el.textContent); } catch { return []; }

    return raw.map((r) => {
      const linkEl = document.getElementById(r.urlElementId);
      return { min: r.min, max: r.max, url: linkEl ? linkEl.getAttribute('href') : '' };
    });
  }

  /* ---- Validation du bouton Continuer ---- */

  _updateNextBtn(questionEl) {
    const btn = questionEl.querySelector('[data-action="next"]');
    if (!btn) return;
    const qType = questionEl.dataset.type;

    if (qType === 'multiple') {
      const hasSelection = questionEl.querySelector('.mm-diag__answer.is-selected') !== null;
      btn.disabled = !hasSelection;
    } else if (qType === 'matrix') {
      const rows = [...questionEl.querySelectorAll('.mm-diag__matrix-container')];
      const allAnswered = rows.length > 0 && rows.every(
        (row) => row.querySelector('.mm-diag__matrix-cell.is-selected') !== null
      );
      btn.disabled = !allAnswered;
    }
  }

  /* ---- Avance à la question suivante ou vers l'étape 3 ---- */

  _advance(questionEl) {
    const qIndex = parseInt(questionEl.dataset.question, 10);
    if (qIndex < this.totalQuestions) {
      this._showQuestion(qIndex + 1);
    } else {
      const rawScore = this._computeTotalScore();
      this.state.totalScore = Math.round((rawScore / this.scoreMax) * 100);
      this._saveState();
      this._showStep(3);
    }
  }

  /* ---- Handlers ---- */

  _handleStart() {
    this._showStep(2);
    this._showQuestion(1, false);
    // Initialiser l'état disabled du bouton de la première question
    const firstQ = this.questions[0];
    if (firstQ) this._updateNextBtn(firstQ);
  }

  _handleBack() {
    const { step, currentQuestion } = this.state;
    if (step === 2 && currentQuestion > 1) {
      this._showQuestion(currentQuestion - 1);
    } else if (step === 2 && currentQuestion === 1) {
      this._showStep(1);
    } else if (step === 3) {
      this._showStep(2);
      this._showQuestion(this.totalQuestions, false);
    }
  }

  _handleSingleAnswer(answerEl, questionEl) {
    const qIndex = questionEl.dataset.question;
    const score = parseInt(answerEl.dataset.score, 10) || 0;

    questionEl.querySelectorAll('.mm-diag__answer').forEach((a) => {
      a.classList.remove('is-selected');
      a.setAttribute('aria-pressed', 'false');
    });
    answerEl.classList.add('is-selected');
    answerEl.setAttribute('aria-pressed', 'true');
    this.state.answers[qIndex] = [score];
    this._saveState();

    // Auto-avance après feedback visuel
    setTimeout(() => this._advance(questionEl), 300);
  }

  _handleMultipleAnswer(answerEl, questionEl) {
    const qIndex = questionEl.dataset.question;
    const score = parseInt(answerEl.dataset.score, 10) || 0;
    const isSelected = answerEl.classList.contains('is-selected');

    if (isSelected) {
      answerEl.classList.remove('is-selected');
      answerEl.setAttribute('aria-pressed', 'false');
      this.state.answers[qIndex] = (this.state.answers[qIndex] || []).filter((s) => s !== score);
    } else {
      answerEl.classList.add('is-selected');
      answerEl.setAttribute('aria-pressed', 'true');
      this.state.answers[qIndex] = [...(this.state.answers[qIndex] || []), score];
    }
    this._saveState();
    this._updateNextBtn(questionEl);
  }

  _handleMatrixCell(cellEl, rowEl, questionEl) {
    const qIndex = questionEl.dataset.question;
    const subIdx = rowEl.dataset.sub;
    const score = parseInt(cellEl.dataset.score, 10) || 0;

    rowEl.querySelectorAll('.mm-diag__matrix-cell').forEach((c) => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-pressed', 'false');
    });
    cellEl.classList.add('is-selected');
    cellEl.setAttribute('aria-pressed', 'true');

    if (!this.state.answers[qIndex] || Array.isArray(this.state.answers[qIndex])) {
      this.state.answers[qIndex] = {};
    }
    this.state.answers[qIndex][subIdx] = score;
    this._saveState();
    this._updateNextBtn(questionEl);
  }

  _handleKlaviyoSubmit() {
    const score = this.state.totalScore;
    const resultUrl = this._findResultUrl(score);

    if (!resultUrl) return false;

    const step3 = this.steps[3];
    if (step3) step3.dataset.loading = 'true';

    this._markSubmitted();
    this._clearState();
    const dest = new URL(resultUrl, window.location.origin);
    dest.searchParams.set('score', score);
    window.location.href = dest.toString();
    return true;
  }

  /* ---- Bind events ---- */

  _bindEvents() {
    const startBtn = this.el.querySelector('[data-action="start"]');
    if (startBtn) startBtn.addEventListener('click', () => this._handleStart());

    const backBtn = this.el.querySelector('[data-action="back"]');
    if (backBtn) backBtn.addEventListener('click', () => this._handleBack());

    // Délégation sur l'étape 2 pour tous les clics
    const step2 = this.el.querySelector('[data-step="2"]');
    step2.addEventListener('click', (e) => {
      // Bouton Continuer
      const nextBtn = e.target.closest('[data-action="next"]');
      if (nextBtn) {
        const question = nextBtn.closest('.mm-diag__question');
        if (question) this._advance(question);
        return;
      }

      // Réponse single / multiple
      const answer = e.target.closest('.mm-diag__answer');
      if (answer) {
        const question = answer.closest('.mm-diag__question');
        if (!question) return;
        const qType = question.dataset.type;
        if (qType === 'single') this._handleSingleAnswer(answer, question);
        else if (qType === 'multiple') this._handleMultipleAnswer(answer, question);
        return;
      }

      // Cellule matrix
      const cell = e.target.closest('.mm-diag__matrix-cell');
      if (cell) {
        const row = cell.closest('.mm-diag__matrix-container');
        const question = cell.closest('.mm-diag__question');
        if (row && question) this._handleMatrixCell(cell, row, question);
      }
    });

    // Klaviyo dispatche sur window, pas document
    window.addEventListener('klaviyoForms', (e) => {
      if (e.detail?.type !== 'submit') return;
      if (this.state.step !== 3) return;
      this._handleKlaviyoSubmit();
    });
  }
}

/* ---- Init ---- */
document.querySelectorAll('[data-section-id][id^="mm-diagnostic-"]').forEach((el) => {
  new MMDiagnostic(el);
});
